import { useMutation, useQuery } from "@tanstack/react-query";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { useCallback, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { RiErrorWarningLine } from "react-icons/ri";
import { useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Container, Label, Row, Spinner, CardHeader, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Navigator from "src/components/navigator";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";

const EudrDueDiligenceAddGeoLocation = ({ t }) => {
    const [geoLocationData, setGeoLocationData] = useState({
        geolocationReference: [],
        farmGeolocationReference: [],
        slaughterGeolocationReference: []
    });
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    
    const toggleCancelConfirmModal = () => {
        setShowCancelConfirmModal(!showCancelConfirmModal);
    };

    const location = useLocation();
    const history = useHistory();

    const params = new URLSearchParams(location.search);

    const productId = params.get('product');

    const {
        isFetching: productDetailsLoading,
        data: productData
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-product-detail',
            productId
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.show(productId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!productId,
        onError: () => {
            toast(t('An error occurred while fetching product details.'), {
                type: 'error'
            });
        }
    });

    const {
        mutateAsync: validatePlotMutation
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.validateGeoLocation(payload);
        }
    });

    const {
        mutate: updateProductGeoLocation,
        isLoading: updateIsInProcess
    } = useMutation({
        mutationFn: async ({
            id,
            payload
        }) => {
            const service = SupplierProductService.getInstance();
            return await service.updateGeoLocation(id, payload);
        },
        onSuccess: () => {
            toast(t("Geolocation for this product has been saved successfully."), {
                type: "success"
            });

            history.push('/third-party/3rd-party/eudr-due-diligence');
        },
        onError: () => {
            toast(t("Geolocation for this product could not be saved. Please try again."), {
                type: "error"
            });
        }
    });

    const {
        data: originCountryGeoData,
        isFetching: originCountryGeoDataIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-country-geo-data',
            productData?.originCountry?.id
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountryGeoData(productData?.originCountry?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!productData?.originCountry?.id,
        onError: () => {
            toast(t('Failed to load geolocation data for the selected country.'), {
                type: 'error'
            });
        }
    });

    const plotValidator = useCallback(({ geoJSON }) => {
        return validatePlotMutation({
            ...geoJSON,
            properties: {
                commodity: (productData?.commodity || '').toLowerCase(),
                name: `company-test-${productData?.commodity}`,
                production_end: (productData.importDate || '').split(' ')[0],
            }
        });
    }, [validatePlotMutation, productData]);

    const plotValidationSettings = useMemo(() => {
        return {
            enabled: true,
            validate: plotValidator,
            errorMessage: {
                title: t('Invalid Plot Detected'),
                body: (
                    <>
                        {t('The selected plot did not pass the plausibility verification check.')}
                        <br />
                        {t('This means the plot’s boundaries or geolocation data are not consistent with the expected agricultural area or satellite validation criteria.')}
                        <br />
                        {t('Please review the drawn area and ensure it accurately represents a valid production plot before proceeding.')}
                    </>
                )
            }
        }
    }, [plotValidator, t]);

    const boundaryRestrictionSettings = useMemo(() => {
        return {
            enabled: !!originCountryGeoData?.geojson,
            area: originCountryGeoData?.geojson,
            errorMessage: {
                title: t('Outside Country Boundary'),
                body: (
                    <>
                        {t('The selected area is outside the boundary of the chosen country.')}
                        <br />
                        {t('Please draw your plot within the country limits to ensure valid geolocation data.')}
                    </>
                )
            }
        }
    }, [t, originCountryGeoData?.geojson ]);

    const handleAreaChange = useCallback(({ action, id, geoJSON, name }) => {
        switch (action) {
            case 'create':
                setGeoLocationData((currentState) => {
                    return {
                        ...currentState,
                        [name]: [
                            ...currentState[name],
                            {
                                id: id,
                                geoJSON: geoJSON
                            }
                        ]
                    }
                })
                break;

            case 'edit':
                setGeoLocationData((currentState) => {
                    const foundIndex = currentState[name].findIndex((item) => item.id === id);

                    const newState = { ...currentState };

                    if (foundIndex < 0) {
                        return newState;
                    }

                    newState[name][foundIndex].geoJSON = geoJSON;

                    return newState;
                })
                break;

            case 'delete':
                setGeoLocationData((currentState) => {
                    const newState = { ...currentState };

                    newState[name] = currentState[name].filter((item) => item.id !== id);

                    return newState;
                })
                break;
            default:
                break;
        }
    }, []);

    const handleMapLoad = useCallback((loadedItems, name) => {
        setGeoLocationData((currentState) => {
            const newState = { ...currentState };

            newState[name] = loadedItems;

            return newState;
        });
    }, []);

    const handleSubmit = (e, values) => {
        updateProductGeoLocation({
            id: productId,
            payload: {
                coordinates: values.geolocationReference.map((loc) => loc.geoJSON),
                farmCoordinates: values.farmGeolocationReference.map((loc) => loc.geoJSON),
                slaughterCoordinates: values.slaughterGeolocationReference.map((loc) => loc.geoJSON)
            }
        });
    }

    const renderCancelModal = () => {
        return (
            <Modal isOpen={showCancelConfirmModal} toggle={toggleCancelConfirmModal} centered>
                <ModalHeader toggle={toggleCancelConfirmModal}>
                    <div className="d-flex justify-content-start align-items-center">
                        <RiErrorWarningLine className="me-2 text-warning" size={22} />
                        {t('Cancel Geolocation Entry?')}
                    </div>
                </ModalHeader>
                <ModalBody>
                    {t('You’re about to cancel the geolocation process.')}<br/>
                    {t('Any area or plot you’ve drawn on the map will be discarded.')}<br/>
                    {t('Are you sure you want to exit without saving your changes?')}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleCancelConfirmModal}>
                        {t('Continue Drawing')}
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => history.push('/third-party/3rd-party/eudr-due-diligence')}
                    >
                        {t('Yes, Cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        )
    }

    const renderBody = () => {
        const commodity = (productData?.commodity || '').toLowerCase();

        if(commodity !== 'cattle') {
            return (
                <Card>
                    <CardHeader className="bg-white">
                        <h5 className="mb-1">
                            {t('Geolocation Data')}
                        </h5>
                        <p className="text-muted mb-0">
                            {t('Draw the production area of the product on the map. Make sure the plot is within the country of origin. This information is used to validate EUDR compliance and traceability.')}
                        </p>
                    </CardHeader>

                    <CardBody>
                        <AvForm
                            className="needs-validation m-2"
                            onValidSubmit={handleSubmit}
                        >
                            <Row>
                                <Col sm='12' className="mb-3">
                                    <Label>
                                        {t('Geolocation (Draw Polygon)')}
                                    </Label>

                                    <GeoMap
                                        name="geolocationReference"
                                        onAreaChange={handleAreaChange}
                                        onLoaded={handleMapLoad}
                                        preDefinedGeo={productData?.geolocationReference}
                                        multiDraw={true}
                                        defaultSearchParam={productData?.originCountry?.name}
                                        boundaryRestriction={boundaryRestrictionSettings}
                                        isLoading={originCountryGeoDataIsFetching}
                                        plotValidation={plotValidationSettings}
                                        styles={{
                                            height: '600px'
                                        }}
                                    />

                                    <AvField
                                        name="geolocationReference"
                                        type="hidden"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true }
                                        }}
                                        value={geoLocationData.geolocationReference}
                                    />
                                </Col>

                                <Col sm='12' className="d-flex justify-content-between">
                                    <Button
                                        color="secondary"
                                        outline
                                        type="button"
                                        onClick={() => {
                                            setShowCancelConfirmModal(true)
                                        }}
                                        disabled={updateIsInProcess}
                                    >
                                        {t('Cancel')}
                                    </Button>

                                    <div>
                                        <Button
                                            color="primary"
                                            type="submit"
                                            disabled={updateIsInProcess}
                                        >
                                            {updateIsInProcess && (
                                                <Spinner color="light" size="sm" className="me-2" />
                                            )}
                                            {t('Save Geolocation')}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </AvForm>
                    </CardBody>

                    {renderCancelModal()}
                </Card>
            )
        }
        
        return (
            <Card>
                <CardHeader className="bg-white">
                    <h5 className="mb-1">
                        {t('Geolocation Data')}
                    </h5>
                    <p className="text-muted mb-0">
                        {t('For cattle products, geolocation data must cover the entire production chain — from the animal’s birth to processing.')}{' '}
                        {t('Please draw the locations for the Breeding Farm, Fattening Farm, and Slaughterhouse on the maps below. Each map should accurately represent the area of activity to ensure full traceability and EUDR compliance.')}
                    </p>
                </CardHeader>

                <CardBody>
                    <AvForm
                        className="needs-validation m-2"
                        onValidSubmit={handleSubmit}
                    >
                        <Row>
                            <Col sm='12' className="mb-3">
                                <Label>
                                    {t('Breeding Farm Location')}
                                </Label>

                                <GeoMap
                                    name="geolocationReference"
                                    onAreaChange={handleAreaChange}
                                    onLoaded={handleMapLoad}
                                    preDefinedGeo={productData?.geolocationReference}
                                    multiDraw={true}
                                    defaultSearchParam={productData?.originCountry?.name}
                                    boundaryRestriction={boundaryRestrictionSettings}
                                    isLoading={originCountryGeoDataIsFetching}
                                    plotValidation={plotValidationSettings}
                                    styles={{
                                        height: '400px'
                                    }}
                                />

                                <AvField
                                    name="geolocationReference"
                                    type="hidden"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={geoLocationData.geolocationReference}
                                />
                            </Col>

                            <Col sm='12' className="mb-3">
                                <Label>
                                    {t('Fattening Farm Location')}
                                </Label>

                                <GeoMap
                                    name="farmGeolocationReference"
                                    onAreaChange={handleAreaChange}
                                    onLoaded={handleMapLoad}
                                    preDefinedGeo={productData?.farmGeolocationReference}
                                    multiDraw={true}
                                    defaultSearchParam={productData?.originCountry?.name}
                                    boundaryRestriction={boundaryRestrictionSettings}
                                    isLoading={originCountryGeoDataIsFetching}
                                    plotValidation={plotValidationSettings}
                                    styles={{
                                        height: '400px'
                                    }}
                                />

                                <AvField
                                    name="farmGeolocationReference"
                                    type="hidden"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={geoLocationData.farmGeolocationReference}
                                />
                            </Col>

                            <Col sm='12' className="mb-3">
                                <Label>
                                    {t('Slaughterhouse Location')}
                                </Label>

                                <GeoMap
                                    name="slaughterGeolocationReference"
                                    onAreaChange={handleAreaChange}
                                    onLoaded={handleMapLoad}
                                    preDefinedGeo={productData?.slaughterGeolocationReference}
                                    multiDraw={true}
                                    defaultSearchParam={productData?.originCountry?.name}
                                    boundaryRestriction={boundaryRestrictionSettings}
                                    isLoading={originCountryGeoDataIsFetching}
                                    plotValidation={plotValidationSettings}
                                    styles={{
                                        height: '400px'
                                    }}
                                />

                                <AvField
                                    name="slaughterGeolocationReference"
                                    type="hidden"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={geoLocationData.slaughterGeolocationReference}
                                />
                            </Col>

                            <Col sm='12' className="d-flex justify-content-between">
                                <Button
                                    color="secondary"
                                    outline
                                    type="button"
                                    disabled={updateIsInProcess}
                                    onClick={() => {
                                        setShowCancelConfirmModal(true)
                                    }}
                                >
                                    {t('Cancel')}
                                </Button>

                                <div>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={updateIsInProcess}
                                    >
                                        {updateIsInProcess && (
                                            <Spinner color="light" size="sm" className="me-2" />
                                        )}
                                        {t('Save Geolocation')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </AvForm>
                </CardBody>

                {renderCancelModal()}
            </Card>
        )
    }

    if (!productId) {
        return history.push('/third-party/3rd-party/eudr-due-diligence');
    }

    return (
        <div className='page-content'>
            <Container fluid>
                <Row>
                    <Col sm='12'>
                        <Navigator
                            breadCrumbs={[
                                {
                                    title: 'SIP',
                                    link: '/third-party/3rd-party/home'
                                },
                                {
                                    title: t('My reports'),
                                    link: '/third-party/3rd-party/reports'
                                },
                                {
                                    title: t('EUDR Due Diligence'),
                                    link: '/third-party/3rd-party/eudr-due-diligence'
                                },
                                {
                                    title: t('Add Geolocation'),
                                    link: '#'
                                }
                            ]}
                            pageTitle={t('Add Geolocation')}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col sm='12'>
                        {productDetailsLoading ? (
                            <div className="text-center">
                                <Spinner color="primary" size="sm" className="me-2" />
                                {t('Please wait')}...
                            </div>
                        ) : (
                            renderBody()
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    )
};

export default withNamespaces()(
    EudrDueDiligenceAddGeoLocation
);